import React from 'react';
import { Warehouse, Refrigerator, Archive, Boxes, Box, X } from 'lucide-react';
import { unidadDe } from '../../../lib/helpers/products';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';

/**
 * Icono por tipo de ubicación, coherente con la vista aérea de Ubicaciones.
 *
 * Eran emoji (fábrica, cubito, archivador, caja). Se ven distintos en cada
 * sistema operativo, no heredan el color del texto y no hay forma de ajustarles
 * el grosor: en la tablet del club salían más grandes y más saturados que el
 * resto de la fila.
 * Ahora son Lucide, como el resto del producto.
 */
export function locationIcon(kind, props = {}) {
  const comun = { size: 16, strokeWidth: 1.5, 'aria-hidden': true, ...props };
  // MISMO mapa que la vista aérea (StockBoard.KIND_ICON): si divergen, el operario
  // ve una nave industrial en el diálogo y cajas apiladas en Ubicaciones para la
  // misma nevera. Al añadir un kind nuevo: tocar AMBOS o consolidar en un módulo.
  switch (kind) {
    case 'principal':
      return <Warehouse {...comun} />;
    case 'sub_almacen':
      return <Boxes {...comun} />;
    case 'nevera':
      return <Refrigerator {...comun} />;
    case 'armario':
      return <Archive {...comun} />;
    default:
      return <Box {...comun} />;
  }
}

/**
 * «¿De dónde sale?» — Figma `Club_Dialog_Ubicacion` (4275:81515).
 *
 * Aparece SOLO cuando hay ambigüedad (el producto está en 2+ ubicaciones de
 * sala) o falta stock en la designada. El resto de ventas no muestran nada: la
 * regla de oro decide sola.
 *
 * Tres casos, que son los tres del frame:
 *   varias ubicaciones      «¿De dónde sale?»
 *   no llega en la de siempre «No hay suficiente ahí»
 *   ninguna con stock       «No hay de dónde sacarlo», con dos salidas
 *
 * NO tiene pie de botones en los dos primeros: elegir la ubicación ES la
 * acción. Un «Aceptar» sería un segundo clic para nada.
 *
 * Lo que había antes: cabecera con degradado `purple-700 → purple-900` —un
 * color que no existe en el sistema de diseño—, radios de 16 y el aviso de
 * stock insuficiente en versalitas rojas de 10 px al margen.
 */
export default function LocationChoiceModal({ active, onClose, detail, onChoose, processing, unitType }) {
  // Antes del early return: un hook no puede llamarse condicionalmente.
  const overlayDismiss = useOverlayDismiss(onClose);
  if (!active || !detail) return null;

  const { product_name, reason, needed, options = [] } = detail;
  // unidadDe = el MISMO helper que carta/barra/carrito ('g', no 'gr'): un dato, un render.
  const unidad = unidadDe({ unit_type: unitType });
  const fmt = (q) => {
    const n = Number(q);
    // es-ES como el grid que queda DEBAJO (fmtLocQty): '1.234,5' en las dos capas,
    // no '1,234.5' sobre '1.234,5' para el mismo número.
    return Number.isFinite(n) ? n.toLocaleString('es-ES', { maximumFractionDigits: 3 }) : q;
  };

  const sinOpciones = options.length === 0;
  // El callejón REAL: hay opciones pero ninguna llega (stock partido 2+2 para un
  // pedido de 5). El Dispenser abre el diálogo sin comprobar some(enough) —Barra sí
  // lo hace—, así que sin esto el operario se queda ante filas todas deshabilitadas
  // con un título que le ordena elegir. Mismas salidas que sin opciones.
  const sinSalida = sinOpciones || !options.some((o) => o.enough);
  const ambigua = reason === 'ambiguous';

  const titulo = sinSalida
    ? 'No hay de dónde sacarlo'
    : ambigua
      ? '¿De dónde sale?'
      : 'No hay suficiente ahí';

  // OJO con `needed`: el backend manda el TOTAL que pide la línea (stock_services
  // usa `enough = qty >= needed`), NO lo que falta. Decir «faltan {needed}» con
  // pedido de 3 y stock 2 sería mentir (faltan 1). El copy correcto habla del
  // total a descontar, como el diálogo de siempre.
  const subtitulo = sinSalida
    ? `${product_name} no tiene stock suficiente en ninguna ubicación de sala. Puedes seguir y que se decida al cobrar, o cancelar y revisar el almacén.`
    : ambigua
      ? `${product_name} está en varias ubicaciones. Elige de cuál descontar.`
      : `${product_name}: en la ubicación de siempre no llega. Elige de dónde descontar ${fmt(needed)} ${unidad}.`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-auto bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      {...overlayDismiss}
    >
      {/* 380 de ancho, radio 10, 24 de padding: el patrón de `Club_Alert dialog`. */}
      <div className="flex w-full max-w-[380px] flex-col gap-4 rounded-lg border border-[var(--border)] bg-[var(--background)] p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h2 className="text-lg font-medium text-[var(--foreground)]">{titulo}</h2>
            <p className="text-sm text-[var(--muted-foreground)]">{subtitulo}</p>
          </div>
          {/*
           * La salida. `useOverlayDismiss` no escucha Escape, así que sin esto
           * el único modo de salir sería pulsar fuera: ni se ve ni sirve con
           * teclado. En mitad de una venta, un diálogo del que no se sabe salir
           * es peor que uno feo.
           */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="-m-1 flex-shrink-0 rounded-sm p-1 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
          >
            <X size={16} strokeWidth={1.5} aria-hidden />
          </button>
        </div>

        {sinOpciones ? (
          // Un diálogo sin ninguna acción no debería existir: se ofrece seguir
          // (el backend decide la ubicación) o volverse atrás.
          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={processing}
              onClick={() => onChoose(null)}
              className="inline-flex h-9 w-full items-center justify-center rounded-sm border border-[var(--border)] bg-[var(--background)] px-4 text-sm font-medium text-[var(--foreground)] transition-colors hover:border-[var(--border-hover)] disabled:opacity-60"
            >
              Continuar sin elegir
            </button>
            {/* Aquí sí, además de la ×: es el caso en que el operario se queda
                atascado, y junto a un «continuar» que asume algo conviene que la
                marcha atrás esté a la vista y no solo en la esquina. */}
            <button
              type="button"
              disabled={processing}
              onClick={onClose}
              className="inline-flex h-9 w-full items-center justify-center rounded-sm px-4 text-sm font-medium text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)] disabled:opacity-60"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {options.map((opt) => {
              const alcanza = opt.enough;
              return (
                <button
                  key={opt.location_id}
                  type="button"
                  data-testid={`ubicacion-${opt.location_id}`}
                  data-estado={alcanza ? 'default' : 'sin-stock'}
                  disabled={!alcanza || processing}
                  onClick={() => onChoose(opt.location_id)}
                  /*
                   * Estados de `Club_Ubicacion_Fila`. La que no llega NO reacciona
                   * —ni hover ni pulsado— y eso es parte de lo que cuenta: de ahí
                   * no se puede sacar. Iluminarla invitaría a un clic inútil.
                   */
                  className={`flex w-full items-center justify-between gap-3 rounded-sm border px-4 py-3 text-left transition-colors ${
                    alcanza
                      ? 'cursor-pointer border-[var(--border)] hover:border-[var(--border-hover)] hover:bg-[var(--muted)] active:border-[var(--primary)] active:bg-[var(--primary-subtle-2)]'
                      : 'cursor-not-allowed border-[var(--border)] opacity-60'
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="flex-shrink-0 text-[var(--foreground)]">
                      {locationIcon(opt.kind)}
                    </span>
                    <span className="truncate text-sm font-medium text-[var(--foreground)]">
                      {opt.name}
                    </span>
                  </span>
                  <span className="flex flex-shrink-0 items-center gap-2">
                    <span className="text-sm tabular-nums text-[var(--muted-foreground)]">
                      Stock {fmt(opt.quantity)} {unidad}
                    </span>
                    {!alcanza && (
                      // En palabras y en la fila que lo sufre. Antes eran
                      // versalitas rojas de 10 px, que a un brazo no se leen.
                      <span className="rounded-full bg-[var(--destructive-bg)] px-2 py-0.5 text-xs font-medium text-[var(--destructive)]">
                        No llega
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
            {sinSalida && (
              // Con todas las filas apagadas, las filas informan pero no salvan:
              // las mismas dos salidas que en el caso sin opciones, a la vista.
              <div className="mt-1 flex flex-col gap-2">
                <button
                  type="button"
                  disabled={processing}
                  onClick={() => onChoose(null)}
                  className="inline-flex h-9 w-full items-center justify-center rounded-sm border border-[var(--border)] bg-[var(--background)] px-4 text-sm font-medium text-[var(--foreground)] transition-colors hover:border-[var(--border-hover)] disabled:opacity-60"
                >
                  Continuar sin elegir
                </button>
                <button
                  type="button"
                  disabled={processing}
                  onClick={onClose}
                  className="inline-flex h-9 w-full items-center justify-center rounded-sm px-4 text-sm font-medium text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)] disabled:opacity-60"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
